import type { CheckoutRedespachoInfo, CheckoutShippingOption } from '@/lib/api'

export type CartFreightSelectionSource = 'automatic' | 'manual' | null

export type CartFreightSelection = {
  mode: 'cif' | 'fob'
  selectedOption: CheckoutShippingOption | null
  selectionSource: CartFreightSelectionSource
  usarRedespacho: 0 | 1 | null
  transportadoraRedespachoId: number | null
  redespacho: CheckoutRedespachoInfo | null
}

export type CartFreightQuotePayload = {
  tipo_frete: 'cif' | 'fob'
  transportadora_id: number | null
  transportadora_nome: string | null
  cotacao_id: number | null
  numero_cotacao: string | null
  prazo: string | null
  valor: number
  usar_redespacho: 0 | 1 | null
  transportadora_redespacho_id: number | null
  redespacho: {
    transportadora_id: number
    transportadora_nome: string
    endereco_resumo: string
  } | null
}

export const buildCartFreightQuotePayload = (
  selection: CartFreightSelection | null | undefined
): CartFreightQuotePayload | null => {
  const selectedOption = selection?.selectedOption ?? null
  const hasManualFreightChoice = Boolean(
    selection?.selectionSource === 'manual'
    && (selectedOption || selection?.mode === 'fob')
  )
  if (!selection || !hasManualFreightChoice) return null

  const selectedRedespacho = selection.redespacho ?? selectedOption?.redespacho ?? null
  const mode = selection.mode ?? selectedOption?.tipoFrete ?? 'cif'
  const usarRedespacho = mode === 'fob' ? 0 : selection.usarRedespacho
  const transportadoraRedespachoId = usarRedespacho === 1
    ? selection.transportadoraRedespachoId ?? selectedRedespacho?.transportadoraId ?? null
    : null

  return {
    tipo_frete: mode,
    transportadora_id: selectedOption?.transportadoraId ?? null,
    transportadora_nome: selectedOption?.label ?? null,
    cotacao_id: selectedOption?.cotacaoId ?? null,
    numero_cotacao: selectedOption?.quoteNumber ?? null,
    prazo: selectedOption?.eta ?? null,
    valor: mode === 'fob' ? 0 : Math.max(0, selectedOption?.price ?? 0),
    usar_redespacho: usarRedespacho,
    transportadora_redespacho_id: transportadoraRedespachoId,
    redespacho: usarRedespacho === 1 && selectedRedespacho && transportadoraRedespachoId
      ? {
          transportadora_id: transportadoraRedespachoId,
          transportadora_nome: selectedRedespacho.transportadoraNome,
          endereco_resumo: selectedRedespacho.enderecoResumo,
        }
      : null,
  }
}

export const hasManualCartFreightModeSelection = (
  selection: CartFreightSelection | null | undefined
) => selection?.selectionSource === 'manual'

export const hasManualCartFreightCarrierSelection = (
  selection: CartFreightSelection | null | undefined
) => (
  hasManualCartFreightModeSelection(selection)
  && Boolean(selection?.selectedOption)
)

export const resolveAllowedCartFreightCarrierId = (params: {
  selectionSource: CartFreightSelectionSource
  selectedTransportadoraId: number | null | undefined
  explicitPrefillTransportadoraId?: number | null
}) => {
  const manualTransportadoraId = Math.trunc(Number(params.selectedTransportadoraId ?? 0))
  if (params.selectionSource === 'manual' && manualTransportadoraId > 0) {
    return manualTransportadoraId
  }

  const explicitPrefillTransportadoraId = Math.trunc(Number(params.explicitPrefillTransportadoraId ?? 0))
  return explicitPrefillTransportadoraId > 0 ? explicitPrefillTransportadoraId : null
}
